/**
 * FundraiserActivityTab.tsx - Fundraiser Activity Timeline Tab
 *
 * This component displays the activity timeline for fundraisers including
 * analytics, charts, and detailed activity information in a tabbed interface.
 *
 * Key Features:
 * - Activity timeline with chronological events
 * - Analytics dashboard with charts and metrics
 * - Donation insights and performance analysis
 * - Fundraiser details summary and key information
 * - Progress tracking and goal analysis
 * - Promoter performance charts and statistics
 *
 * Activity Sections:
 * - Fundraiser Details: Key information and status
 * - Progress Section: Goal progress and timeline
 * - Daily Donations Chart: Donation trends over time
 * - Donation Insights: Analysis and patterns
 * - Promoters Chart: Promoter performance analytics
 *
 * Analytics Features:
 * - Daily donation trends and patterns
 * - Promoter performance comparisons
 * - Goal progress tracking and projections
 * - Donor behavior analysis
 * - Campaign effectiveness metrics
 *
 * Props: None (uses MobX store for selected fundraiser data)
 *
 * Dependencies: Activity tab components, chart components, MobX store
 */

import React from "react";
import { observer } from "mobx-react-lite";
import { UpdateStatusModal } from "../UpdateStatusModal";
import FundraiserDetailsSection from "./activity-tab/FundraiserDetailsSection";
import { ProgressSection } from "./activity-tab/ProgressSection";
import { DailyDonationsChart } from "./activity-tab/DailyDonationsChart";
import { PromotersChart } from "./activity-tab/PromotersChart";
import { DonationInsightsSection } from "./activity-tab/DonationInsightsSection";
import { CreateFundraiserModel } from "../../models";
interface FundraiserActivityTabProps {
  fundraiser: CreateFundraiserModel;
  handleStatusUpdate: (refresh?: boolean) => void;
}

export const FundraiserActivityTab: React.FC<FundraiserActivityTabProps> =
  observer(({ fundraiser, handleStatusUpdate }) => {
    const [showStatusModal, setShowStatusModal] = React.useState(false);

    const handleStatusClick = () => {
      setShowStatusModal(true);
    };

    const handleStatusClose = (refresh?: boolean) => {
      setShowStatusModal(false);
      if (handleStatusUpdate && refresh) {
        handleStatusUpdate(refresh);
      }
    };

    return (
      <>
        <FundraiserDetailsSection
          fundraiser={fundraiser}
          onStatusClick={handleStatusClick}
        />

        <ProgressSection fundraiser={fundraiser} />

        <DailyDonationsChart fundraiser={fundraiser} />

        <PromotersChart fundraiser={fundraiser} />

        <DonationInsightsSection fundraiser={fundraiser} />

        <UpdateStatusModal
          isVisible={showStatusModal}
          onClose={(refresh?: boolean) => handleStatusClose(refresh)}
          fundraiser={fundraiser}
        />
      </>
    );
  });
